import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { osaAPI } from '../../services/api'
import { humanSessionAPI } from '../../services/humanSessionAPI'
import { triageAPI } from '../../services/triageAPI'
import HumanPracticeMode from './HumanPracticeMode'
import TriageTraining from './TriageTraining'
import SideBySideTraining from './SideBySideTraining'

function VerdictBadge({ verdict, score }) {
  const cls = verdict === 'aligned' ? 'badge-success'
    : verdict === 'partial' ? 'badge-warning'
    : verdict === 'misaligned' ? 'badge-error'
    : 'badge-ghost'
  return (
    <span className={`badge ${cls} gap-1`}>
      {verdict || '—'}
      {typeof score === 'number' && <span className="font-mono">{score.toFixed(2)}</span>}
    </span>
  )
}

function PhaseCard({ phase, evaluationPhase }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="card bg-base-100 border border-base-300 shadow-sm">
      <div className="card-body p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="text-xs uppercase tracking-wide text-base-content/60 font-mono">
              Phase {phase.phase_number} · {phase.month_label}
            </div>
            <h3 className="text-base font-semibold mt-1">{phase.expected_action}</h3>
            <div className="text-sm text-base-content/70 mt-1">
              <span className="font-medium">Reveal to clinician:</span> {phase.reveal_to_clinician}
            </div>
          </div>
          {evaluationPhase && (
            <VerdictBadge verdict={evaluationPhase.verdict} score={evaluationPhase.score} />
          )}
        </div>

        <div className="mt-3">
          <div className="text-xs uppercase tracking-wide text-base-content/60">Assessment</div>
          <p className="text-sm whitespace-pre-wrap mt-1">{phase.assessment_text}</p>
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-3">
          {(phase.assessment_citations || []).map((c) => (
            <span key={c} className="badge badge-outline badge-sm font-mono">{c}</span>
          ))}
          <div className="ml-auto text-xs text-base-content/60 font-mono">
            top retrieval score: {(phase.retrieval_top_score || 0).toFixed(3)} ·
            latency: {phase.latency_ms}ms ·
            blinding violations: {phase.blinding_violations}
          </div>
        </div>

        {phase.memo && (
          <div className="alert mt-3 py-2 text-sm">
            <span className="font-semibold">MEMO:</span> {phase.memo}
          </div>
        )}

        <button
          className="btn btn-ghost btn-xs mt-2"
          onClick={() => setOpen(!open)}
        >
          {open ? 'Hide' : 'Show'} dialogue ({(phase._dialogue?.length || 0)} turns)
        </button>
        {open && (
          <div className="mt-2 space-y-2 max-h-96 overflow-y-auto border-t border-base-200 pt-2">
            {(phase._dialogue || []).map((t, i) => (
              <div key={i} className={`text-sm px-3 py-2 rounded ${t.speaker === 'clinician' ? 'bg-primary/5' : 'bg-base-200'}`}>
                <div className="text-xs uppercase tracking-wide text-base-content/60 font-mono">
                  {t.speaker_name} {t.kind ? `· ${t.kind}` : ''}
                </div>
                <div className="mt-1 whitespace-pre-wrap">{t.content}</div>
              </div>
            ))}
          </div>
        )}

        {evaluationPhase?.rationale && (
          <div className="text-xs italic text-base-content/70 mt-2">
            judge: {evaluationPhase.rationale}
          </div>
        )}
      </div>
    </div>
  )
}

function OverallSummary({ evaluation, journey }) {
  if (!evaluation || evaluation.error) return null
  const providers = Object.entries(evaluation.providers || {})
  return (
    <div className="card bg-base-100 border border-primary/30 shadow-sm">
      <div className="card-body p-4">
        <h3 className="text-lg font-bold">Evaluation</h3>
        <div className="text-sm text-base-content/70">
          Teaching point: <span className="italic">{evaluation.teaching_point || '—'}</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
          {providers.map(([name, pr]) => (
            <div key={name} className="rounded border border-base-300 p-3">
              <div className="text-xs uppercase tracking-wide text-base-content/60 font-mono">{name}</div>
              <div className="text-2xl font-bold mt-1">{pr.overall.toFixed(2)}</div>
              <div className="text-xs mt-1 space-y-0.5">
                <div>phase mean: <span className="font-mono">{pr.phase_alignment_mean.toFixed(2)}</span></div>
                <div>diagnosis: <span className="font-mono">{pr.final_diagnosis.score.toFixed(2)}</span></div>
                <div>treatment: <span className="font-mono">{pr.treatment.score.toFixed(2)}</span></div>
                <div>red flags: <span className="font-mono">{pr.red_flag.score.toFixed(2)}</span></div>
              </div>
            </div>
          ))}
        </div>
        <div className="text-sm mt-3">
          KB chunks cited across journey: {(journey?.osa?.kb_chunks_used || []).map((k) => (
            <span key={k} className="badge badge-outline badge-sm font-mono mr-1">{k}</span>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── Comparison card (AI vs Human per phase) ──────────────────────────────────

function ComparisonCard({ phase, aiEvalPhase, humanPhaseScore, humanTurns }) {
  const [showAI, setShowAI] = useState(false)
  const [showHuman, setShowHuman] = useState(false)

  const humanVerdict = humanPhaseScore?.verdict_mean
  const humanScore = humanPhaseScore?.score_mean
  const humanRationale = humanPhaseScore
    ? Object.values(humanPhaseScore.providers || {})[0]?.rationale
    : null

  return (
    <div className="card bg-base-100 border border-base-300 shadow-sm">
      <div className="card-body p-4">
        <div className="text-xs uppercase tracking-wide text-base-content/60 font-mono">
          Phase {phase.phase_number} · {phase.month_label}
        </div>
        <h3 className="text-base font-semibold mt-1">{phase.expected_action}</h3>
        <div className="text-sm text-base-content/70 mt-1">
          <span className="font-medium">Reveal:</span> {phase.reveal_to_clinician}
        </div>

        <div className="grid grid-cols-2 gap-3 mt-3">
          <div className="rounded border border-base-300 p-3">
            <div className="text-xs text-base-content/60 mb-2 font-semibold">AI Agent</div>
            <VerdictBadge verdict={aiEvalPhase?.verdict} score={aiEvalPhase?.score} />
            {aiEvalPhase?.rationale && (
              <p className="text-xs text-base-content/70 mt-2 italic">{aiEvalPhase.rationale}</p>
            )}
          </div>
          <div className="rounded border border-primary/30 p-3">
            <div className="text-xs text-primary mb-2 font-semibold">You</div>
            {humanPhaseScore ? (
              <>
                <VerdictBadge verdict={humanVerdict} score={humanScore} />
                {humanRationale && (
                  <p className="text-xs text-base-content/70 mt-2 italic">{humanRationale}</p>
                )}
              </>
            ) : (
              <span className="text-xs text-base-content/40">Not attempted</span>
            )}
          </div>
        </div>

        <div className="flex gap-2 mt-3">
          <button className="btn btn-ghost btn-xs" onClick={() => setShowAI(!showAI)}>
            {showAI ? 'Hide' : 'Show'} AI dialogue ({phase._dialogue?.length || 0} turns)
          </button>
          {humanTurns?.length > 0 && (
            <button className="btn btn-ghost btn-xs" onClick={() => setShowHuman(!showHuman)}>
              {showHuman ? 'Hide' : 'Show'} your dialogue ({humanTurns.length} turns)
            </button>
          )}
        </div>

        {showAI && (
          <div className="mt-2 space-y-2 max-h-80 overflow-y-auto border-t border-base-200 pt-2">
            {(phase._dialogue || []).map((t, i) => (
              <div key={i} className={`text-sm px-3 py-2 rounded ${t.speaker === 'clinician' ? 'bg-primary/5' : 'bg-base-200'}`}>
                <div className="text-xs uppercase tracking-wide text-base-content/60 font-mono">
                  {t.speaker_name} {t.kind ? `· ${t.kind}` : ''}
                </div>
                <div className="mt-1 whitespace-pre-wrap">{t.content}</div>
              </div>
            ))}
          </div>
        )}

        {showHuman && humanTurns?.length > 0 && (
          <div className="mt-2 space-y-2 max-h-80 overflow-y-auto border-t border-base-200 pt-2">
            {humanTurns.map((t, i) => (
              <div key={i} className={`text-sm px-3 py-2 rounded ${t.speaker === 'clinician' ? 'bg-primary/8 border border-primary/20' : 'bg-base-200'}`}>
                <div className="text-xs uppercase tracking-wide text-base-content/60 font-mono">
                  {t.speaker === 'clinician' ? 'You' : 'Patient'}{t.turn_type ? ` · ${t.turn_type}` : ''}
                </div>
                <div className="mt-1 whitespace-pre-wrap">{t.text}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Human results panel ──────────────────────────────────────────────────────

function HumanResultsPanel({ humanSummary, aiEvaluation }) {
  if (!humanSummary) return null
  const eval_ = humanSummary.evaluation
  const humanProviders = Object.entries(eval_?.providers || {})
  const aiProviders = Object.entries(aiEvaluation?.providers || {})

  return (
    <div className="card bg-base-100 border border-primary/30 shadow-sm">
      <div className="card-body p-4">
        <h3 className="text-lg font-bold text-primary">Your Performance vs AI Agent</h3>
        {eval_?.teaching_point && (
          <div className="text-sm text-base-content/70">
            Teaching point: <span className="italic">{eval_.teaching_point}</span>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
          {aiProviders.length > 0 && (
            <div>
              <div className="text-xs text-base-content/60 font-semibold mb-2 uppercase tracking-wide">AI Agent</div>
              <div className="grid grid-cols-2 gap-2">
                {aiProviders.map(([name, pr]) => (
                  <div key={name} className="rounded border border-base-300 p-2">
                    <div className="text-xs font-mono text-base-content/60">{name}</div>
                    <div className="text-xl font-bold">{pr.overall.toFixed(2)}</div>
                    <div className="text-xs space-y-0.5 mt-1">
                      <div>phase: <span className="font-mono">{pr.phase_alignment_mean.toFixed(2)}</span></div>
                      <div>dx: <span className="font-mono">{pr.final_diagnosis.score.toFixed(2)}</span></div>
                      <div>rx: <span className="font-mono">{pr.treatment.score.toFixed(2)}</span></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {humanProviders.length > 0 && (
            <div>
              <div className="text-xs text-primary font-semibold mb-2 uppercase tracking-wide">You</div>
              <div className="grid grid-cols-2 gap-2">
                {humanProviders.map(([name, pr]) => (
                  <div key={name} className="rounded border border-primary/30 p-2">
                    <div className="text-xs font-mono text-primary/70">{name}</div>
                    <div className="text-xl font-bold text-primary">{pr.overall?.toFixed(2) ?? '—'}</div>
                    <div className="text-xs space-y-0.5 mt-1">
                      <div>phase: <span className="font-mono">{pr.phase_alignment_mean?.toFixed(2) ?? '—'}</span></div>
                      <div>dx: <span className="font-mono">{pr.final_diagnosis?.score?.toFixed(2) ?? '—'}</span></div>
                      <div>rx: <span className="font-mono">{pr.treatment?.score?.toFixed(2) ?? '—'}</span></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function SimulationControls({ config, onChange, archetypes }) {
  const isBenchmark = config.benchmark_mode

  const handlePreset = (tag) => {
    if (tag === 'custom') {
      onChange({ ...config, persona_tag: null })
      return
    }
    const arch = archetypes.find((a) => a.tag === tag)
    if (arch) {
      onChange({
        ...config,
        persona_tag: tag,
        entropy: arch.entropy_mean,
        correctness: arch.correctness_mean,
        benchmark_mode: false,
      })
    }
  }

  return (
    <div className="card bg-base-100 border border-base-300 p-3">
      <div className="text-xs uppercase tracking-wide text-base-content/60 font-semibold mb-3">
        Patient Simulation
      </div>

      <div className="mb-3">
        <label className="text-xs text-base-content/70 mb-1 block">Persona Preset</label>
        <select
          className="select select-sm select-bordered w-full"
          value={config.persona_tag || 'custom'}
          onChange={(e) => handlePreset(e.target.value)}
          disabled={isBenchmark}
        >
          <option value="custom">Custom (manual sliders)</option>
          {archetypes.map((a) => (
            <option key={a.tag} value={a.tag}>{a.tag}</option>
          ))}
        </select>
      </div>

      <div className="mb-3">
        <div className="flex justify-between text-xs text-base-content/70 mb-1">
          <span>Coherence (Entropy)</span>
          <span className="font-mono">{config.entropy.toFixed(2)}</span>
        </div>
        <div className="flex gap-1 items-center text-xs text-base-content/50 mb-1">
          <span>Focused</span>
          <input
            type="range" min="0" max="1" step="0.05"
            className="range range-xs range-primary flex-1 mx-1"
            value={config.entropy}
            disabled={isBenchmark}
            onChange={(e) => onChange({ ...config, entropy: parseFloat(e.target.value), persona_tag: null })}
          />
          <span>Rambling</span>
        </div>
      </div>

      <div className="mb-3">
        <div className="flex justify-between text-xs text-base-content/70 mb-1">
          <span>Medical Accuracy (Correctness)</span>
          <span className="font-mono">{config.correctness.toFixed(2)}</span>
        </div>
        <div className="flex gap-1 items-center text-xs text-base-content/50 mb-1">
          <span>Confused</span>
          <input
            type="range" min="0" max="1" step="0.05"
            className="range range-xs range-secondary flex-1 mx-1"
            value={config.correctness}
            disabled={isBenchmark}
            onChange={(e) => onChange({ ...config, correctness: parseFloat(e.target.value), persona_tag: null })}
          />
          <span>Accurate</span>
        </div>
      </div>

      <label className="label cursor-pointer justify-start gap-2 py-1">
        <input
          type="checkbox"
          className="checkbox checkbox-sm checkbox-accent"
          checked={isBenchmark}
          onChange={(e) => onChange({ ...config, benchmark_mode: e.target.checked, entropy: e.target.checked ? 0 : config.entropy, correctness: e.target.checked ? 1 : config.correctness })}
        />
        <div>
          <div className="text-sm">Benchmark mode</div>
          <div className="text-xs text-base-content/60">Ideal patient, maximal clarity</div>
        </div>
      </label>
    </div>
  )
}

function PopulationPanel({ selected, providers, archetypes }) {
  const [nSamples, setNSamples] = useState(5)
  const [personaTag, setPersonaTag] = useState(null)
  const [running, setRunning] = useState(false)
  const [results, setResults] = useState(null)
  const [error, setError] = useState(null)
  const [expandedRun, setExpandedRun] = useState(null)

  const runPop = async () => {
    if (!selected) return
    setRunning(true); setError(null); setResults(null); setExpandedRun(null)
    try {
      const providerList = Object.entries(providers).filter(([, v]) => v).map(([k]) => k)
      const d = await osaAPI.populationRun({
        caseId: selected,
        personaTag,
        nSamples,
        providers: providerList,
      })
      setResults(d)
    } catch (e) {
      setError(e?.response?.data?.detail || e.message)
    } finally {
      setRunning(false)
    }
  }

  const overallScore = (run) => {
    const eval_ = run.evaluation
    if (!eval_ || eval_.error || !eval_.overall_mean) return null
    return eval_.overall_mean
  }

  return (
    <div className="card bg-base-100 border border-base-300 p-3">
      <div className="text-xs uppercase tracking-wide text-base-content/60 font-semibold mb-2">
        Population Run
      </div>

      <div className="flex items-center gap-2 mb-2">
        <label className="text-xs text-base-content/70">Archetype:</label>
        <select
          className="select select-xs select-bordered flex-1"
          value={personaTag || ''}
          onChange={(e) => setPersonaTag(e.target.value || null)}
        >
          <option value="">Any (default prior)</option>
          {archetypes.map((a) => (
            <option key={a.tag} value={a.tag}>{a.tag}</option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <label className="text-xs text-base-content/70">N samples:</label>
        <input
          type="number" min="1" max="20"
          className="input input-xs input-bordered w-20"
          value={nSamples}
          onChange={(e) => setNSamples(Math.min(20, Math.max(1, parseInt(e.target.value) || 1)))}
        />
      </div>

      <button
        className={`btn btn-outline btn-sm w-full ${running ? 'btn-disabled' : ''}`}
        onClick={runPop}
        disabled={running || !selected}
      >
        {running ? <span className="loading loading-spinner loading-xs"></span> : null}
        {running ? `Running ${nSamples} simulations…` : 'Run Population Simulation'}
      </button>

      {error && <div className="alert alert-error text-xs mt-2">{String(error)}</div>}

      {results && (
        <div className="mt-3">
          {results.summary?.n > 0 && (
            <div className="text-xs text-base-content/70 mb-2 font-mono">
              mean: {results.summary.mean_score?.toFixed(3)} ·
              min: {results.summary.min_score?.toFixed(3)} ·
              max: {results.summary.max_score?.toFixed(3)} ·
              std: {results.summary.std_score?.toFixed(3)}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="table table-xs w-full">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Entropy</th>
                  <th>Accuracy</th>
                  <th>Score</th>
                </tr>
              </thead>
              <tbody>
                {(results.runs || []).map((run, i) => {
                  const bc = run.behavior_config
                  const score = overallScore(run)
                  const isExpanded = expandedRun === i
                  return (
                    <tr
                      key={i}
                      className="cursor-pointer hover:bg-base-200"
                      onClick={() => setExpandedRun(isExpanded ? null : i)}
                    >
                      <td className="font-mono">{i + 1}</td>
                      <td className="font-mono">{bc?.entropy?.toFixed(2) ?? '—'}</td>
                      <td className="font-mono">{bc?.correctness?.toFixed(2) ?? '—'}</td>
                      <td className="font-mono">{score !== null ? score.toFixed(3) : run.error ? '✗' : '—'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          {expandedRun !== null && results.runs[expandedRun] && (() => {
            const run = results.runs[expandedRun]
            const phases = run.journey?.osa?.phases || []
            if (run.journey?.episodes) {
              for (const ep of run.journey.episodes) {
                const p = phases.find((ph) => ph.phase_number === ep.episode_number)
                if (p) p._dialogue = ep.dialogue
              }
            }
            const firstProv = run.evaluation?.providers ? Object.values(run.evaluation.providers)[0] : null
            return (
              <div className="mt-2 border-t border-base-200 pt-2 space-y-2">
                <div className="text-xs text-base-content/60 font-mono">
                  Run {expandedRun + 1} · entropy={run.behavior_config?.entropy?.toFixed(2)} · correctness={run.behavior_config?.correctness?.toFixed(2)}
                </div>
                {phases.map((p) => {
                  const evalPhase = firstProv?.per_phase?.find((x) => x.phase_number === p.phase_number)
                  return <PhaseCard key={p.phase_number} phase={p} evaluationPhase={evalPhase} />
                })}
              </div>
            )
          })()}
        </div>
      )}
    </div>
  )
}

export default function OSACockpit() {
  const navigate = useNavigate()
  const [cases, setCases] = useState([])
  const [triageCases, setTriageCases] = useState([])
  const [triageBuckets, setTriageBuckets] = useState({})
  const [archetypes, setArchetypes] = useState([])
  const [selected, setSelected] = useState(null)
  const [result, setResult] = useState(null)
  const [running, setRunning] = useState(false)
  const [error, setError] = useState(null)
  const [providers, setProviders] = useState({ openai: true, anthropic: false, gemini: false })
  const [simConfig, setSimConfig] = useState({ entropy: 0.3, correctness: 0.8, persona_tag: null, benchmark_mode: false })
  const [humanSession, setHumanSession] = useState(null)
  const [humanSummary, setHumanSummary] = useState(null)
  const [humanPhaseScores, setHumanPhaseScores] = useState({})
  const [humanAllTurns, setHumanAllTurns] = useState({})
  const [humanLoading, setHumanLoading] = useState(false)
  const [humanError, setHumanError] = useState(null)
  const [useWhisper, setUseWhisper] = useState(false)

  useEffect(() => {
    osaAPI.listCases().then((d) => {
      setCases(d.cases || [])
      const def = (d.cases || []).find((c) => c.case_id === 'patient34') || (d.cases || [])[0]
      if (def) setSelected(def.case_id)
    }).catch((e) => setError(e.message || 'failed to load cases'))

    osaAPI.getPersonaPriors().then((d) => {
      setArchetypes(d.archetypes || [])
    }).catch(() => {})

    triageAPI.getCases().then((d) => setTriageCases(d.cases || [])).catch(() => {})
    triageAPI.getTaxonomy().then((d) => {
      const map = {}
      for (const b of d.buckets || []) map[b.id] = b
      setTriageBuckets(map)
    }).catch(() => {})
  }, [])

  const run = async () => {
    if (!selected) return
    setRunning(true); setError(null); setResult(null)
    setHumanSession(null); setHumanSummary(null); setHumanPhaseScores({}); setHumanAllTurns({}); setHumanError(null)
    try {
      const providerList = Object.entries(providers).filter(([, v]) => v).map(([k]) => k)
      const behaviorConfig = {
        entropy: simConfig.entropy,
        correctness: simConfig.correctness,
        persona_tag: simConfig.persona_tag,
        benchmark_mode: simConfig.benchmark_mode,
      }
      const d = await osaAPI.run({ caseId: selected, providers: providerList, behaviorConfig })
      setResult(d)
    } catch (e) {
      setError(e?.response?.data?.detail || e.message)
    } finally {
      setRunning(false)
    }
  }

  const journey = result?.journey
  const evaluation = result?.evaluation
  const phases = journey?.osa?.phases || []

  if (journey?.episodes) {
    for (const ep of journey.episodes) {
      const phasePhone = phases.find((p) => p.phase_number === ep.episode_number)
      if (phasePhone) phasePhone._dialogue = ep.dialogue
    }
  }

  const firstProvider = evaluation?.providers ? Object.values(evaluation.providers)[0] : null

  const startHumanSession = async () => {
    setHumanLoading(true); setHumanError(null); setHumanSummary(null)
    try {
      const providerList = Object.entries(providers).filter(([, v]) => v).map(([k]) => k)
      const d = await humanSessionAPI.start({ caseId: selected, behaviorConfig: simConfig, providers: providerList })
      setHumanSession(d)
    } catch (e) {
      setHumanError(e?.response?.data?.detail || e.message)
    } finally {
      setHumanLoading(false)
    }
  }

  const handleHumanSummary = (summary, phaseScores, allTurns) => {
    setHumanSummary(summary)
    setHumanPhaseScores(phaseScores)
    setHumanAllTurns(allTurns)
    setHumanSession(null)
  }

  const isTriage = triageCases.some((c) => c.id === selected)
  const selectedTriage = triageCases.find((c) => c.id === selected)
  const selectedCase = cases.find((c) => c.case_id === selected)
  const providerList = Object.entries(providers).filter(([, v]) => v).map(([k]) => k)

  return (
    <div className="min-h-screen bg-base-200">
      <div className="navbar bg-base-100 shadow-sm px-6 sticky top-0 z-10">
        <div className="flex-1 flex items-center">
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/menu')}>← Menu</button>
          <span className="text-xl font-bold ml-3">OSA Multi-Agent Cockpit</span>
          <span className="badge badge-ghost ml-3 font-mono text-xs">
            {isTriage ? 'Triage training · agent vs trainee' : 'Fundamentals 19 · grounded'}
          </span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6">
        {/* LEFT: cases + controls */}
        <div className="space-y-3">
          <h2 className="text-sm uppercase tracking-wide text-base-content/60 font-semibold">Patient Roster</h2>
          {cases.map((c) => (
            <button
              key={c.case_id}
              className={`text-left w-full card bg-base-100 border-2 transition-colors p-3 ${selected === c.case_id ? 'border-primary' : 'border-base-300 hover:border-base-content/30'}`}
              onClick={() => { setSelected(c.case_id); setResult(null) }}
            >
              <div className="font-mono text-xs text-primary">{c.case_id}</div>
              <div className="font-semibold text-sm mt-1">{c.name.replace(/Patient \d+\w? — /, '')}</div>
              <div className="text-xs text-base-content/70 mt-1">{c.one_liner}</div>
              <div className="text-xs mt-2">
                <span className="badge badge-ghost badge-sm">{c.phase_count} phases</span>
              </div>
            </button>
          ))}

          {triageCases.length > 0 && (
            <>
              <h2 className="text-sm uppercase tracking-wide text-base-content/60 font-semibold pt-2">Triage Training</h2>
              {triageCases.map((c) => (
                <button
                  key={c.id}
                  className={`text-left w-full card bg-base-100 border-2 transition-colors p-3 ${selected === c.id ? 'border-secondary' : 'border-base-300 hover:border-base-content/30'}`}
                  onClick={() => { setSelected(c.id); setResult(null) }}
                >
                  <div className="font-mono text-xs text-secondary">{c.id}</div>
                  <div className="font-semibold text-sm mt-1">{c.label.replace(/Patient \d+\w? — /, '')}</div>
                  <div className="text-xs text-base-content/70 mt-1">{c.one_liner}</div>
                  <div className="text-xs mt-2">
                    <span className="badge badge-secondary badge-outline badge-sm">{triageBuckets[c.expected_bucket]?.label?.split(' ')[0] || c.expected_bucket}</span>
                  </div>
                </button>
              ))}
            </>
          )}

          {!isTriage && (
            <SimulationControls config={simConfig} onChange={setSimConfig} archetypes={archetypes} />
          )}

          <div className="card bg-base-100 border border-base-300 p-3">
            <div className="text-xs uppercase tracking-wide text-base-content/60 font-semibold mb-2">Judges</div>
            {Object.entries(providers).map(([name, on]) => (
              <label key={name} className="label cursor-pointer justify-start gap-2 py-1">
                <input
                  type="checkbox"
                  className="checkbox checkbox-sm"
                  checked={on}
                  onChange={(e) => setProviders({ ...providers, [name]: e.target.checked })}
                />
                <span className="text-sm">{name}</span>
              </label>
            ))}
            <label className="flex items-center gap-2 text-xs cursor-pointer select-none mt-3">
              <input
                type="checkbox"
                className="checkbox checkbox-xs"
                checked={useWhisper}
                onChange={(e) => setUseWhisper(e.target.checked)}
              />
              Use Whisper transcription (voice)
            </label>
            <p className="text-xs text-base-content/60 mt-2">
              {isTriage
                ? 'These judges score the triage assessments. Start the session on the right.'
                : 'These judges score both you and the agent each phase. Start the session on the right.'}
            </p>
          </div>

          {!isTriage && <PopulationPanel selected={selected} providers={providers} archetypes={archetypes} />}
        </div>

        {/* RIGHT: results */}
        <div className="space-y-4">
          {isTriage ? (
            <TriageTraining
              caseInfo={selectedTriage}
              buckets={triageBuckets}
              providers={providerList}
              useWhisper={useWhisper}
            />
          ) : selectedCase ? (
            <SideBySideTraining
              caseInfo={selectedCase}
              providers={providerList}
              simConfig={simConfig}
              useWhisper={useWhisper}
            />
          ) : (
            <div className="card bg-base-100 border border-base-300 p-6 text-center text-base-content/70">
              <div className="text-4xl mb-3">🫁</div>
              <p>Select a patient on the left to begin a side-by-side training session.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
